/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Icon } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { LuisText } from 'src/core/components/LuisText';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { Intent } from 'src/luis/modules/intents/Intent';
import { UtteranceWithAnalytics } from 'src/luis/projects/utterances/types/UtteranceWithAnalytics';
import { getNearestRivalIntent } from 'src/luis/projects/utterances/utils/utteranceUtils';

type DifferenceCellProps = {
    /**
     * The utterance to be displayed.
     */
    utterance: UtteranceWithAnalytics;

    /**
     * The intents list for the possible nearest rivals intents.
     */
    intents: Intent[];
};

const tableTheme = getScopedTheme('UtteranceTable');

/**
 * Displays The difference cell in the utterance table.
 */
export const DifferenceCell = (props: DifferenceCellProps) => {
    const { utterance, intents } = props;
    const defaultRender = (
        <Stack verticalFill verticalAlign="center">
            <span>–</span>
        </Stack>
    );

    if (!utterance.predictionStore) {
        return defaultRender;
    }

    const labeledIntent = utterance.predictionStore.predictedIntents.find(pI => pI.intentId === utterance.utteranceStore.labeledIntentId);
    const nearestIntent = getNearestRivalIntent(utterance, intents);

    if (!nearestIntent) {
        return defaultRender;
    }

    const difference = Number(labeledIntent.score.toFixed(2)) - Number(nearestIntent.score.toFixed(2));
    let iconName = '';
    let color = '';

    if (utterance.analytics?.incorrectIntents.length) {
        iconName = 'SkypeCircleMinus';
        color = tableTheme.incorrectColor;
    } else if (utterance.analytics?.unclearIntents.length) {
        iconName = 'IncidentTriangle';
        color = tableTheme.unclearColor;
    } else if (difference < 0) {
        iconName = 'SkypeCircleMinus';
        color = tableTheme.incorrectColor;
    }

    return (
        <Stack verticalFill verticalAlign="center" horizontal tokens={{ childrenGap: 16 }}>
            <LuisText color={color}>{`${difference.toFixed(2)}`}</LuisText>
            <Icon hidden={!iconName} iconName={iconName} styles={{ root: { color } }} />
        </Stack>
    );
};
